import React from "react";
import {
    SectionCard,
    mutedButtonClass,
    primaryButtonClass,
} from "../adminShared";

const sqlTextareaClass =
    "min-h-[320px] w-full rounded-xl border border-slate-300 bg-slate-950 px-3 py-3 font-mono text-sm text-slate-100 outline-none transition placeholder:text-slate-500 focus:border-slate-500 focus:ring-4 focus:ring-slate-200";

const fileInputClass =
    "block w-full cursor-pointer rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm file:mr-4 file:rounded-lg file:border-0 file:bg-slate-900 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-slate-800";

const jsonValue = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "object") {
        try {
            return JSON.stringify(value);
        } catch {
            return String(value);
        }
    }
    return String(value);
};

function SqlResultTable({ result }) {
    const rows = Array.isArray(result?.rows) ? result.rows : [];
    const columns = Array.isArray(result?.columns) && result.columns.length > 0
        ? result.columns
        : rows.length > 0
            ? Object.keys(rows[0] || {})
            : [];

    if (!result) {
        return <div className="text-sm text-slate-500">No SQL has been run yet.</div>;
    }

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 font-semibold text-slate-700">
                    Statement: {result.statement_type || "-"}
                </span>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 font-semibold text-slate-700">
                    Statements: {result.statement_count ?? 1}
                </span>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 font-semibold text-slate-700">
                    Rows: {result.row_count ?? rows.length}
                </span>
                {result.message ? (
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 font-semibold text-slate-700">
                        {result.message}
                    </span>
                ) : null}
            </div>

            {rows.length > 0 ? (
                <div className="max-w-[120rem] overflow-x-auto rounded-xl border border-slate-200">
                    <table className="min-w-full table-auto whitespace-nowrap text-left text-sm leading-5">
                        <thead className="bg-slate-50 text-slate-600">
                            <tr>
                                {columns.map((column) => (
                                    <th key={column} className="px-3 py-2 whitespace-nowrap">
                                        {column}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 bg-white">
                            {rows.map((row, idx) => (
                                <tr key={idx}>
                                    {columns.map((column) => (
                                        <td key={column} className="px-3 py-2 whitespace-nowrap text-slate-700">
                                            {jsonValue(row?.[column])}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                    Script executed, but no result rows were returned.
                </div>
            )}
        </div>
    );
}

export default function SqlEditorTab({
    working,
    sqlQuery,
    setSqlQuery,
    sqlQueryResult,
    sqlQueryRunning,
    runSqlQuery,
    clearSqlQueryResult,
    loadSqlFile,
    sqlFileName,
}) {
    return (
        <SectionCard
            title="SQL Editor"
            description="Run SQL against the live database. Paste a query, or load a .sql file such as migrate_data_pg16_seed.sql."
            right={
                <div className="flex flex-wrap gap-2">
                    <button
                        className={mutedButtonClass}
                        onClick={clearSqlQueryResult}
                        disabled={working || sqlQueryRunning}
                    >
                        Clear result
                    </button>
                    <button
                        className={primaryButtonClass}
                        onClick={runSqlQuery}
                        disabled={working || sqlQueryRunning}
                    >
                        {sqlQueryRunning ? "Running..." : "Run SQL"}
                    </button>
                </div>
            }
        >
            <div className="space-y-4 text-left">
                <p className="text-sm text-slate-500">
                    Admin only. This tab supports large multi-statement scripts. Query results are shown when the last statement returns rows.
                </p>

                <div className="grid gap-3 lg:grid-cols-[1fr_320px]">
                    <div>
                        <label className="mb-2 block text-sm font-semibold text-slate-700">
                            SQL / Script
                        </label>
                        <textarea
                            className={sqlTextareaClass}
                            value={sqlQuery}
                            onChange={(e) => setSqlQuery(e.target.value)}
                            spellCheck={false}
                            placeholder="Paste a SQL statement or an entire SQL script here"
                        />
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="mb-2 block text-sm font-semibold text-slate-700">
                                Load SQL file
                            </label>
                            <input
                                type="file"
                                accept=".sql,.txt,text/plain,application/sql"
                                className={fileInputClass}
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    loadSqlFile(file);
                                    e.target.value = "";
                                }}
                                disabled={working || sqlQueryRunning}
                            />
                            <p className="mt-2 text-xs text-slate-500">
                                {sqlFileName ? `Loaded: ${sqlFileName}` : "No file loaded."}
                            </p>
                        </div>

                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                            Tips:
                            <ul className="mt-2 space-y-1">
                                <li>Use `SELECT` for inspection.</li>
                                <li>Paste full seed scripts if you want to replay them.</li>
                                <li>Large files are fine, but execution happens on the live admin database.</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <SqlResultTable result={sqlQueryResult} />
                </div>
            </div>
        </SectionCard>
    );
}
